import { Alert, Button, Card, CardFooter, Image } from '@heroui/react';
import { Edit2, Scaling } from 'lucide-react';
import React, { FC, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ActionBlock,
  ContentLoadError,
  DataGrid,
  Flex,
  Grid,
  PageTitle,
  PageWrapper,
  Spinner,
  Text,
  TranslationSwitcher,
} from '../../../components';
import {
  useGetDeviceDocumentFromFirestore,
  useQueryHelper,
} from '../../../queries';
import { useAuthState, useLangState } from '../../../state';

export const Device: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { defaultLang } = useAuthState();
  const { languages } = useLangState();
  const [selectedLang, setSelectedLang] = useState<string>(defaultLang);

  const deviceDocument = useGetDeviceDocumentFromFirestore(id);
  const { data: device, refetch } = deviceDocument;
  const { isLoading, isError, errorMessage } = useQueryHelper({
    queries: [deviceDocument],
  });

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }
  return (
    <div>
      <PageWrapper>
        <PageTitle
          title="Device City"
          actions={(
            <Button
              variant="solid"
              size="sm"
              color="primary"
              startContent={<Edit2 size={14} color="#fff" />}
              onPress={() => navigate(`devices/${id}/edit`)}
            >
              Edit
            </Button>
          )}
        />

        <ActionBlock>
          <Grid
            gridTemplateColumns="3fr 9fr"
            justifyContent="start"
            itemAlign="center"
            gap="25px"
          >
            <div>
              {device?.homeScreenImg ? (
                <Flex gap="small" itemAlign="center" justifyContent="flex-start" margin="25px 0">

                  <Card isFooterBlurred className="border-none" radius="lg">

                    <Image
                      alt="cover photo"
                      src={device?.homeScreenImg}
                      width="100%"
                      height="auto"
                    />
                    <CardFooter className="justify-center before:bg-white/10 border-white/20 border-1 overflow-hidden py-1 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%_-_8px)] shadow-small ml-1 z-10">
                      <p className="text-tiny text-white/80">Welcome screen cover image.</p>
                    </CardFooter>
                  </Card>
                </Flex>
              ) : <Alert color="danger" title="No home screen image detected" />}
            </div>
            <div>
              <DataGrid data={[
                { label: 'Name', content: device?.name },
                {
                  label: 'Color',
                  content:
  <Flex key={`device-size-${device?.model.size}`} itemAlign="center" justifyContent="center" gap="5px">
    {device?.model?.color === 'White' ? (
      <Flex itemAlign="center" justifyContent="flex-start" gap="5px">
        <div
          className="flex items-center justify-center w-8 h-8 border border-black bg-white rounded-full"
        />
      </Flex>
    ) : (
      <Flex itemAlign="center" justifyContent="flex-start" gap="5px">
        <div
          className="flex items-center justify-center w-8 h-8 border border-black bg-black rounded-full"
        />
      </Flex>
    )}
  </Flex>,
                },
                {
                  label: 'Size',
                  content:
  <Flex key={`device-size-${device?.model.size}`} itemAlign="center">
    <Scaling size={24} color="#000" />
    {device?.model.size}
    ''
  </Flex>,
                },
                { label: 'ID', content: device?.serial_id },
                { label: 'Internal Survey', content: device?.surveyId ? 'Yes' : 'No' },
                { label: 'Web View', content: device?.webView ? 'Yes' : 'No' },
              ]}
              />
            </div>

          </Grid>
        </ActionBlock>

        <ActionBlock
          title="Welcome Text"
          action={(
            <TranslationSwitcher
              languages={languages}
              currentLang={selectedLang}
              onSelectedLangChange={(lang) => setSelectedLang(lang)}
            />
        )}
        >
          <Flex gap="15px" itemAlign="center" justifyContent="flex-start" margin="15px 0">
            <ReactCountryFlag
              key={selectedLang}
              countryCode={selectedLang === 'en' ? 'gb' : selectedLang}
              svg
              style={{
                cursor: 'pointer',
                width: 18,
                height: 14,
                background: '#fff',
                borderRadius: '2px',
              }}
            />
            <Text data={device?.homeScreenWelcomeText[selectedLang]} size="extraLarge" isBold />
          </Flex>
        </ActionBlock>

        <ActionBlock title="Section Visable">
          <DataGrid
            dataRows={3}
            data={[
              { label: 'For kids', content: device?.settings?.showSection?.forkids ? 'Yes' : 'No' },
              { label: 'Culture', content: device?.settings?.showSection?.culture ? 'Yes' : 'No' },
              { label: 'Trips', content: device?.settings?.showSection?.trips ? 'Yes' : 'No' },
              { label: 'Other Places', content: device?.settings?.showSection?.otherplaces ? 'Yes' : 'No' },
            ]}
          />
        </ActionBlock>
        <ActionBlock title="Menus">
          <DataGrid
            dataRows={3}
            data={[
              {
                label: 'Menu slider',
                content:
  <Flex gap="small" direction="row" itemAlign="center">
    {device?.settings?.menuSettings?.slideMenu?.map((i) => <p key={i} className="mr-2 text-xs">{i}</p>)}
    {!!device?.settings && <p className="mr-2 text-xs text-red-500">No menu</p>}
  </Flex>,
              },
              {
                label: 'HomeScreen Menu',
                content:
  <Flex gap="small" direction="row" itemAlign="center">
    {device?.settings?.menuSettings?.homeScreen?.map((i) => <p key={i} className="mr-2 text-xs">{i}</p>)}
    {!!device?.settings && <p className="mr-2 text-xs text-red-500">No menu</p>}
  </Flex>,
              },
              {
                label: 'WelcomeScreen Menu',
                content:
  <Flex gap="small" direction="row" itemAlign="center">
    {device?.settings?.menuSettings?.welcomeScreen?.map((i) => <p key={i} className="mr-2 text-xs">{i}</p>)}
    {!!device?.settings && <p className="mr-2 text-xs text-red-500">No menu</p>}
  </Flex>,
              },

            ]}
          />
        </ActionBlock>
      </PageWrapper>
    </div>
  );
};
